import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { useToast } from '@aimsos/ui';

import { reportTruthFactIssue } from '../../../services/truth.js';

/**
 * "Report an issue" on a Truth fact (CT-5719), as on a trait: submits directly,
 * says how it went in a toast, and reports once on a double click.
 *
 * A success is final for this fact: `reported` stays true and the ref stays
 * closed, so the button does not come back while the fact is re-read — nor if
 * that re-read fails. Only a failed report opens it again.
 */
export function useReportTruthFactIssue({ entityType, entityId, factId, onReported } = {}) {
  const { t } = useTranslation('unified-profiles');
  const toast = useToast();
  const [reporting, setReporting] = useState(false);
  const [reported, setReported] = useState(false);
  const submittingRef = useRef(false);

  const report = useCallback(async () => {
    if (submittingRef.current) return false;
    submittingRef.current = true;
    setReporting(true);
    try {
      await reportTruthFactIssue({ entityType, entityId, factId });
      setReported(true);
      toast.success({ title: t('knowledge.truth.toast.reportedTitle'), message: t('knowledge.truth.toast.reportedMessage') });
      onReported?.();
      return true;
    } catch (err) {
      submittingRef.current = false;
      toast.error({
        title: t('knowledge.truth.toast.reportErrorTitle'),
        message: err?.message ?? t('knowledge.truth.toast.errorMessage'),
      });
      return false;
    } finally {
      setReporting(false);
    }
  }, [entityType, entityId, factId, onReported, toast, t]);

  return useMemo(() => ({ report, reporting, reported }), [report, reporting, reported]);
}

export default useReportTruthFactIssue;
